import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Calendar } from 'lucide-react';
import { publicGalleryApi, PublicGalleryInfo, PublicPhoto } from '../api/publicGallery';
import { MachhaCharacter } from '../lib/machhaAsset';
import { Button } from '../components/UI/Button';
import { usePageTitle } from '../hooks/usePageTitle';
import { pageVariants } from '../constants/animations';
import styles from './PublicGalleryPage.module.css';

type PageState =
  | { kind: 'loading' }
  | { kind: 'pin-prompt'; info: PublicGalleryInfo }
  | { kind: 'gallery'; info: PublicGalleryInfo; accessToken: string | null }
  | { kind: 'expired' }
  | { kind: 'not-found' }
  | { kind: 'error' };

export function PublicGalleryPage() {
  const { token } = useParams<{ token: string }>();
  const [pageState, setPageState] = useState<PageState>({ kind: 'loading' });
  const [photos, setPhotos] = useState<PublicPhoto[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // PIN prompt state
  const [pinValue, setPinValue] = useState('');
  const [pinError, setPinError] = useState('');
  const [verifyingPin, setVerifyingPin] = useState(false);

  const loadingMoreRef = useRef(false);
  const accessTokenRef = useRef<string | null>(null);

  const albumName =
    pageState.kind === 'gallery' || pageState.kind === 'pin-prompt'
      ? pageState.info.albumName
      : undefined;

  usePageTitle(albumName);

  // Fetch gallery metadata on mount
  useEffect(() => {
    if (!token) {
      setPageState({ kind: 'not-found' });
      return;
    }
    setPageState({ kind: 'loading' });
    publicGalleryApi
      .getInfo(token)
      .then((info) => {
        if (info.hasPassword) {
          setPageState({ kind: 'pin-prompt', info });
        } else {
          setPageState({ kind: 'gallery', info, accessToken: null });
        }
      })
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 404) {
          setPageState({ kind: 'not-found' });
        } else if (status === 410) {
          setPageState({ kind: 'expired' });
        } else {
          setPageState({ kind: 'error' });
        }
      });
  }, [token]);

  // Load first page of photos when gallery becomes accessible
  const loadPhotos = useCallback(
    async (accessToken: string | null, cursor?: string) => {
      if (!token) return;
      const result = await publicGalleryApi.getPhotos(token, {
        cursor,
        accessToken: accessToken ?? undefined,
      });
      return result;
    },
    [token],
  );

  useEffect(() => {
    if (pageState.kind !== 'gallery') return;
    accessTokenRef.current = pageState.accessToken;
    loadPhotos(pageState.accessToken)
      .then((result) => {
        if (!result) return;
        setPhotos(result.photos);
        setNextCursor(result.nextCursor);
        setHasMore(result.hasMore);
      })
      .catch(() => {
        /* silent — page is already rendered */
      });
  }, [pageState.kind === 'gallery' ? pageState.accessToken : null]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMore = useCallback(async () => {
    if (!nextCursor || loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const result = await loadPhotos(accessTokenRef.current, nextCursor);
      if (!result) return;
      setPhotos((prev) => [...prev, ...result.photos]);
      setNextCursor(result.nextCursor);
      setHasMore(result.hasMore);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [nextCursor, loadPhotos]);

  const loadMoreRef = useRef(loadMore);
  useEffect(() => { loadMoreRef.current = loadMore; }, [loadMore]);

  // IntersectionObserver sentinel for infinite scroll
  const observerRef = useRef<IntersectionObserver | null>(null);
  const sentinelCallbackRef = useCallback((el: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!el) return;
    observerRef.current = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMoreRef.current(); },
      { rootMargin: '300px' },
    );
    observerRef.current.observe(el);
  }, []);

  const handleVerifyPin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !pinValue) return;
    setVerifyingPin(true);
    setPinError('');
    try {
      const { accessToken } = await publicGalleryApi.verifyPin(token, pinValue);
      accessTokenRef.current = accessToken;
      if (pageState.kind === 'pin-prompt') {
        setPageState({ kind: 'gallery', info: pageState.info, accessToken });
      }
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 401) {
        setPinError('Incorrect PIN. Please try again.');
      } else {
        setPinError('Something went wrong. Please try again.');
      }
    } finally {
      setVerifyingPin(false);
    }
  };


  // ── Loading ──────────────────────────────────────────────────────────────
  if (pageState.kind === 'loading') {
    return (
      <div className={styles['skeleton-container']}>
        <div className={styles['skeleton-title']} />
        <div className={styles['skeleton-meta']} />
        <div className={styles['photo-grid']}>
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className={styles['skeleton-card']} />
          ))}
        </div>
      </div>
    );
  }

  // ── Expired ──────────────────────────────────────────────────────────────
  if (pageState.kind === 'expired') {
    return (
      <div className={styles['state-screen']}>
        <MachhaCharacter state="Rate Limit" size={180} />
        <h2 className={styles['state-title']}>This link has expired</h2>
        <p className={styles['state-subtitle']}>
          The creator's share link is no longer active.
        </p>
      </div>
    );
  }

  // ── Not found ────────────────────────────────────────────────────────────
  if (pageState.kind === 'not-found' || pageState.kind === 'error') {
    return (
      <div className={styles['state-screen']}>
        <MachhaCharacter state="Not Found" size={180} />
        <h2 className={styles['state-title']}>Gallery not found</h2>
        <p className={styles['state-subtitle']}>
          This share link doesn't exist or has been revoked.
        </p>
      </div>
    );
  }

  // ── PIN prompt ───────────────────────────────────────────────────────────
  if (pageState.kind === 'pin-prompt') {
    return (
      <div className={styles['pin-wrapper']}>
        <motion.div
          className={styles['pin-card']}
          variants={pageVariants}
          initial="hidden"
          animate="visible"
        >
          <div className={styles['pin-icon-wrap']}>
            <Lock size={22} />
          </div>
          <h2 className={styles['pin-title']}>{pageState.info.albumName}</h2>
          <p className={styles['pin-subtitle']}>
            This gallery is PIN-protected. Enter the PIN to view photos.
          </p>
          <form onSubmit={handleVerifyPin}>
            <input
              type="password"
              inputMode="numeric"
              className={styles['pin-input']}
              placeholder="• • • •"
              value={pinValue}
              onChange={(e) => setPinValue(e.target.value)}
              autoFocus
              maxLength={20}
            />
            {pinError && <p className={styles['pin-error']}>{pinError}</p>}
            <div className={styles['pin-btn-wrap']}>
              <Button
                type="submit"
                variant="primary"
                loading={verifyingPin}
                disabled={!pinValue}
              >
                View Gallery
              </Button>
            </div>
          </form>
        </motion.div>
      </div>
    );
  }

  // ── Gallery ──────────────────────────────────────────────────────────────
  const { info } = pageState;

  const expiresLabel = info.expiresAt
    ? `Expires ${new Date(info.expiresAt).toLocaleDateString()}`
    : null;

  return (
    <motion.div
      className={styles['page']}
      variants={pageVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header */}
      <div className={styles['header']}>
        <div className={styles['brand-row']}>
          <span className={styles['brand-logo']}>Pixel Machha · Shared Gallery</span>
        </div>
        <h1 className={styles['album-title']}>{info.albumName}</h1>
        <div className={styles['header-meta']}>
          <span className={styles['photo-count']}>
            {info.photoCount} photo{info.photoCount !== 1 ? 's' : ''}
          </span>
          {expiresLabel && (
            <span className={styles['expiry-badge']}>
              <Calendar size={12} />
              {expiresLabel}
            </span>
          )}
        </div>
      </div>

      {/* Photo grid */}
      {photos.length === 0 && !loadingMore ? (
        <div className={styles['empty-state']}>
          <MachhaCharacter state="Empty" size={140} />
          <p className={styles['empty-state-text']}>No photos in this gallery yet.</p>
        </div>
      ) : (
        <>
          <div className={styles['photo-grid']}>
            {photos.map((photo) => (
              <div key={photo._id} className={styles['photo-card']}>
                <img
                  src={photo.thumbnailUrl}
                  alt="Gallery photo"
                  loading="lazy"
                />
              </div>
            ))}
            {loadingMore &&
              Array.from({ length: 6 }).map((_, i) => (
                <div key={`sk-${i}`} className={styles['skeleton-card']} />
              ))}
          </div>
          {hasMore && (
            <div ref={sentinelCallbackRef} className={styles['sentinel']} />
          )}
        </>
      )}
    </motion.div>
  );
}
